import { format, isSameDay } from 'date-fns';
import { useEffect, useState } from 'react';
import { DayPicker, DayPickerProps } from 'react-day-picker';
import { useTranslation } from 'react-i18next';
import 'react-day-picker/dist/style.css';
import { Popover, PopoverProps } from '@anytag/shared-atoms';
import { DATE_PICKER_INPUT_FORMAT, DEFAULT_DATE_FNS_FORMAT } from '@anytag/shared-constants';
import { localeUtils } from '@anytag/shared-utils';
import { ActionButtons } from '../action-buttons';
import { CustomDayPickerNav, CustomInput, WithGlobalStyles } from './components';
import { getDatePickerLocale, getDefaultDate, placeholderSingle } from './helpers';
import { PickerDate } from './types';

export interface DayPickerSingleProps {
  value: string;
  handleChangeDay: (day: string) => void;
  hasError?: boolean;
  disabled?: boolean;
  className?: string;
  disabledDays?: DayPickerProps['disabled'];
  saveOnBlur?: boolean;
  clearable?: boolean;
  placeholder?: string;
  withinPortal?: boolean;
  targetProps?: PopoverProps['targetProps'];
}
export const DayPickerSingle = ({
  value,
  disabled,
  hasError,
  className,
  targetProps,
  disabledDays,
  handleChangeDay,
  saveOnBlur = true,
  clearable = true,
  placeholder = placeholderSingle,
  withinPortal = true
}: DayPickerSingleProps) => {
  const { i18n } = useTranslation();
  const [selectedDate, setSelectedDate] = useState<PickerDate>(getDefaultDate(value));
  let handleClosePopover: () => void;

  const resultInput = selectedDate ? format(selectedDate, DATE_PICKER_INPUT_FORMAT) : '';

  useEffect(() => {
    if (selectedDate && !isSameDay(selectedDate, value)) {
      setSelectedDate(getDefaultDate(value));
    }
  }, [value]); // Do not add selectedDate to dependency array cause infinite loop

  const handleDayClick = (day: PickerDate) => {
    setSelectedDate(day);
  };

  const handleUpdate = () => {
    handleClosePopover();
    handleChangeDay(selectedDate ? format(selectedDate, DEFAULT_DATE_FNS_FORMAT) : '');
  };

  const handleCancel = () => {
    setSelectedDate(getDefaultDate(value));
  };

  return (
    <div className={className}>
      <Popover
        withinPortal={withinPortal}
        onClose={saveOnBlur ? handleUpdate : handleCancel}
        targetProps={targetProps}
        target={({ setOpen }) => (
          <CustomInput
            clearable={clearable}
            hasError={!!hasError}
            disabled={!!disabled}
            value={resultInput || placeholder}
            onClick={setOpen}
            onClear={() => {
              handleChangeDay('');
            }}
          />
        )}
        align="start"
        side="bottom"
        withBorder
      >
        {({ closePopover }) => {
          handleClosePopover = closePopover;

          return (
            <WithGlobalStyles>
              <DayPicker
                mode="single"
                numberOfMonths={2}
                selected={selectedDate || undefined}
                onSelect={() => void 0} // http://github.com/gpbl/react-day-picker/issues/2690
                onDayClick={handleDayClick}
                locale={getDatePickerLocale(i18n.language)}
                defaultMonth={selectedDate || new Date()}
                weekStartsOn={1}
                disabled={disabledDays}
                components={{
                  Nav: CustomDayPickerNav
                }}
                formatters={{
                  formatCaption: (captionLabelDate) => localeUtils.formatMonthTitle(captionLabelDate, i18n.language)
                }}
              />
              <ActionButtons
                primaryButtonProps={{
                  onClick: handleUpdate
                }}
                secondaryButtonProps={{
                  onClick: () => {
                    handleCancel();
                    handleClosePopover();
                  }
                }}
                buttonHeight="32px"
                hasTopBorder
              >
                {resultInput}
              </ActionButtons>
            </WithGlobalStyles>
          );
        }}
      </Popover>
    </div>
  );
};
